import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Badge } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function PaletteCustomization() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState('');
  const [palette, setPalette] = useState(null);
  const [selectedColors, setSelectedColors] = useState([]); // Array of color keys to regenerate
  const [shadePickerColor, setShadePickerColor] = useState(null); // Which color is showing shade picker

  // Helper function to convert hex to HSL
  const hexToHsl = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return { h: 0, s: 0, l: 50 };

    let r = parseInt(result[1], 16) / 255;
    let g = parseInt(result[2], 16) / 255;
    let b = parseInt(result[3], 16) / 255;

    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    return { h: h * 360, s: s * 100, l: l * 100 };
  };

  // Helper function to convert HSL to hex
  const hslToHex = (h, s, l) => {
    s /= 100;
    l /= 100;
    const a = s * Math.min(l, 1 - l);
    const f = n => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
  };

  // Generate shade variations for a color
  const generateShades = (hexColor) => {
    const hsl = hexToHsl(hexColor);
    const shades = [];
    // Generate 9 shades: from much lighter to much darker
    const lightnessSteps = [90, 80, 70, 60, 50, 40, 30, 20, 10];

    for (const l of lightnessSteps) {
      shades.push({
        hex: hslToHex(hsl.h, hsl.s, l),
        lightness: l,
        label: l > 60 ? 'Lighter' : l < 40 ? 'Darker' : 'Base'
      });
    }
    return shades;
  };

  useEffect(() => {
    checkAuthAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    await loadSavedPalette();
  };

  const loadSavedPalette = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/brand/get-saved-palette', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPalette(data.palette);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'No saved palette found');
      }
    } catch (err) {
      setError('Error loading palette: ' + err.message);
    }
    setLoading(false);
  };

  const toggleColorSelection = (colorKey) => {
    if (selectedColors.includes(colorKey)) {
      setSelectedColors(selectedColors.filter(k => k !== colorKey));
    } else {
      setSelectedColors([...selectedColors, colorKey]);
    }
  };

  const toggleShadePicker = (colorKey, e) => {
    e.stopPropagation(); // Don't trigger color selection
    setShadePickerColor(shadePickerColor === colorKey ? null : colorKey);
  };

  const applyShade = (colorKey, newHex) => {
    setPalette({
      ...palette,
      [colorKey]: newHex
    });
    setShadePickerColor(null); // Close the shade picker
  };

  const handleRegenerateSelected = async () => {
    if (selectedColors.length === 0) return;

    setRegenerating(true);
    setError('');

    try {
      const response = await fetch('/api/brand/regenerate-colors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          current_palette: palette,
          colors_to_replace: selectedColors
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPalette(data.palette);
        setSelectedColors([]); // Clear selection after regeneration
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to regenerate colors');
      }
    } catch (err) {
      setError('Error regenerating colors: ' + err.message);
    }

    setRegenerating(false);
  };

  const handleSavePalette = async () => {
    try {
      const response = await fetch('/api/brand/save-color-palette', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ palette })
      });

      if (response.ok) {
        navigate('/dashboard/branding');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save palette');
      }
    } catch (err) {
      setError('Error saving palette: ' + err.message);
    }
  };

  const ColorCard = ({ colorKey, color, name }) => {
    const isSelected = selectedColors.includes(colorKey);
    const showingShades = shadePickerColor === colorKey;
    const shades = showingShades ? generateShades(color) : [];

    return (
      <Card
        className={`mb-3 ${isSelected ? 'border-warning border-3' : ''}`}
        style={{ cursor: 'pointer' }}
        onClick={() => toggleColorSelection(colorKey)}
      >
        <Card.Body className="p-3">
          <div
            style={{
              backgroundColor: color,
              height: '100px',
              borderRadius: '8px',
              border: '2px solid #ddd',
              marginBottom: '12px',
              position: 'relative'
            }}
          >
            {isSelected && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(255, 193, 7, 0.9)',
                padding: '8px 16px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '14px'
              }}>
                🔄 Will Regenerate
              </div>
            )}
          </div>
          <div className="text-center">
            <strong>{name}</strong>
            <br />
            <code style={{ fontSize: '0.85rem' }}>{color}</code>
            <br />
            <span
              style={{
                color: '#007bff',
                cursor: 'pointer',
                fontSize: '0.85rem',
                textDecoration: 'underline'
              }}
              onClick={(e) => toggleShadePicker(colorKey, e)}
            >
              {showingShades ? '✕ Close' : '◐ Change Shade'}
            </span>
          </div>

          {/* Shade Picker */}
          {showingShades && (
            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <small className="text-muted d-block mb-2 text-center">
                Click a shade to apply:
              </small>
              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {shades.map((shade, idx) => (
                  <div
                    key={idx}
                    onClick={() => applyShade(colorKey, shade.hex)}
                    style={{
                      width: '32px',
                      height: '32px',
                      backgroundColor: shade.hex,
                      borderRadius: '4px',
                      border: shade.hex === color ? '3px solid #000' : '1px solid #ccc',
                      cursor: 'pointer',
                      transition: 'transform 0.1s'
                    }}
                    title={`${shade.hex} (${shade.lightness}%)`}
                    onMouseEnter={(e) => e.target.style.transform = 'scale(1.15)'}
                    onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                  />
                ))}
              </div>
              <small className="text-muted d-block mt-2 text-center" style={{ fontSize: '0.7rem' }}>
                Lighter ← → Darker
              </small>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading your palette...</p>
      </Container>
    );
  }

  if (error && !palette) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Alert variant="danger">{error}</Alert>
            <Button variant="primary" onClick={() => navigate('/dashboard/color-palettes')}>
              Choose a Palette
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!palette) {
    return (
      <Container className="py-5 text-center">
        <p>No palette found</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="branding" />
      <Row className="justify-content-center">
        <Col md={10} lg={8}>
          <Card className="shadow">
            <Card.Body className="p-5">
              <h1 className="text-center mb-2">🎨 Customize Your Palette</h1>
              <p className="text-center text-muted mb-4">
                Click on any colors you want to replace, then regenerate just those colors
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              {regenerating && (
                <Alert variant="info" className="text-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <strong>Regenerating selected colors...</strong> Claude is creating harmonious replacements.
                </Alert>
              )}

              <Alert variant="info" className="mb-4">
                <strong>Palette Name:</strong> {palette.name}
                <br />
                <small>{palette.description}</small>
              </Alert>

              <div style={{ opacity: regenerating ? 0.5 : 1, transition: 'opacity 0.3s' }}>
                {/* Primary & Secondary */}
                <h5 className="mb-3">Primary & Secondary Colors</h5>
                <Row className="mb-4">
                  <Col md={6}>
                    <ColorCard
                      colorKey="primary"
                      color={palette.primary}
                      name={palette.primary_name}
                    />
                  </Col>
                  <Col md={6}>
                    <ColorCard
                      colorKey="secondary"
                      color={palette.secondary}
                      name={palette.secondary_name}
                    />
                  </Col>
                </Row>

                {/* Accent Colors */}
                <h5 className="mb-3">Accent Colors</h5>
                <Row className="mb-4">
                  <Col md={4}>
                    <ColorCard
                      colorKey="accent1"
                      color={palette.accent1}
                      name={palette.accent1_name}
                    />
                  </Col>
                  <Col md={4}>
                    <ColorCard
                      colorKey="accent2"
                      color={palette.accent2}
                      name={palette.accent2_name}
                    />
                  </Col>
                  <Col md={4}>
                    <ColorCard
                      colorKey="accent3"
                      color={palette.accent3}
                      name={palette.accent3_name}
                    />
                  </Col>
                </Row>

                {/* Neutral Colors */}
                <h5 className="mb-3">Neutral Colors</h5>
                <Row className="mb-4">
                  <Col md={6}>
                    <ColorCard
                      colorKey="neutral1"
                      color={palette.neutral1}
                      name={palette.neutral1_name}
                    />
                  </Col>
                  <Col md={6}>
                    <ColorCard
                      colorKey="neutral2"
                      color={palette.neutral2}
                      name={palette.neutral2_name}
                    />
                  </Col>
                </Row>
              </div>

              {selectedColors.length > 0 && (
                <Alert variant="warning" className="mb-4">
                  <strong>Selected for regeneration:</strong> {selectedColors.length} color{selectedColors.length > 1 ? 's' : ''}
                  <br />
                  <small>Click "Regenerate Selected Colors" to get new options for just these colors</small>
                </Alert>
              )}

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate('/dashboard/color-palettes')}
                >
                  ← Back to All Palettes
                </Button>

                <div>
                  <Button
                    variant="warning"
                    className="me-3"
                    onClick={handleRegenerateSelected}
                    disabled={selectedColors.length === 0 || regenerating}
                  >
                    {regenerating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Regenerating...
                      </>
                    ) : (
                      <>
                        🔄 Regenerate Selected ({selectedColors.length})
                      </>
                    )}
                  </Button>

                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleSavePalette}
                  >
                    Save & Continue →
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default PaletteCustomization;
